import { useEffect, useMemo, useState, useCallback, useRef } from "react";
import { listDrafts, previewDraft, deleteDraft, addAccountToDraft, removeAccountFromDraft } from "../api/invoices";
import api from "../api/axios";
import type { Client as FakturowniaClient } from "../types/client";
import { colors, spacing, borderRadius, typography, createButtonStyle, shadows } from "../styles/theme";
import { getAccountDeals, assignDealToAccount, AccountWithDeal, BitrixDeal } from "../api/bitrix";
import BitrixDealSearch from "./BitrixDealSearch";

type AdsAccountInfo = {
  source_type: string;
  client_id: string;
  display_name: string;
  is_from_position: boolean;  // True = from position, False = manually added (deletable)
};

type AllegroClient = { id: string; nickname: string };
type GoogleAdsClient = { customer_id: string; descriptive_name: string; mcc_email?: string };
type Client = AllegroClient | GoogleAdsClient;

type Draft = {
  id: number;
  fakturownia_client_id: number;
  fakturownia_client_name?: string;
  name: string;
  date_policy: "rolling_30" | "previous_month" | string;
  anchor_start_date?: string | null;
  department_name: "semtop" | "adtarget";  // NOWE
  allegro_client_ids: string[];  // Deprecated - backward compatibility
  allegro_nicknames?: string[];  // Deprecated
  ads_accounts?: AdsAccountInfo[];  // New unified field
  active: boolean;
  has_percent: boolean;
};

type PreviewOfDraft = Awaited<ReturnType<typeof previewDraft>>;

// Smart pagination helper - generates page numbers with ellipsis
const generatePageNumbers = (currentPage: number, totalPages: number): (number | string)[] => {
  if (totalPages <= 7) {
    return Array.from({ length: totalPages }, (_, i) => i + 1);
  }

  const pages: (number | string)[] = [];

  // Always show first page
  pages.push(1);

  // Calculate range around current page
  let start = Math.max(2, currentPage - 2);
  let end = Math.min(totalPages - 1, currentPage + 2);

  // If near the beginning, show more pages on the left
  if (currentPage <= 4) {
    start = 2;
    end = Math.min(6, totalPages - 1);
  }

  // If near the end, show more pages on the right
  if (currentPage >= totalPages - 3) {
    start = Math.max(2, totalPages - 5);
    end = totalPages - 1;
  }

  // Add ellipsis if there's a gap after first page
  if (start > 2) {
    pages.push('...');
  }

  // Add pages around current
  for (let i = start; i <= end; i++) {
    pages.push(i);
  }

  // Add ellipsis if there's a gap before last page
  if (end < totalPages - 1) {
    pages.push('...');
  }

  // Always show last page
  if (totalPages > 1) {
    pages.push(totalPages);
  }

  return pages;
};

const DraftsList = () => {
  const [items, setItems] = useState<Draft[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const [selected, setSelected] = useState<Record<number, boolean>>({});
  const [previews, setPreviews] = useState<Record<number, PreviewOfDraft | null>>({});
  const [previewLoading, setPreviewLoading] = useState<Record<number, boolean>>({});
  const [fcMap, setFcMap] = useState<Record<number, FakturowniaClient>>({});

  // Bitrix deal info for accounts
  const [bitrixDeals, setBitrixDeals] = useState<Record<string, AccountWithDeal>>({});
  const [editingBitrixAccount, setEditingBitrixAccount] = useState<{ sourceType: string; clientId: string } | null>(null);

  const anySelected = useMemo(() => Object.values(selected).some(Boolean), [selected]);

  // FILTRY
  const [q, setQ] = useState("");
  const [accountQ, setAccountQ] = useState("");  // Changed from allegroQ to accountQ
  const [companyFilter, setCompanyFilter] = useState<'semtop' | 'adtarget' | 'all'>('all');  // NOWE

  // PAGINATION
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 12;
  const totalPages = Math.ceil(totalItems / itemsPerPage);

  const load = useCallback(async () => {
    setError(null);
    setLoading(true);
    try {
      const fcIdMaybe = /^\d+$/.test(q.trim()) ? Number(q.trim()) : undefined;
      const offset = (currentPage - 1) * itemsPerPage;
      const data = await listDrafts(
        fcIdMaybe,
        fcIdMaybe ? undefined : q.trim() || undefined,
        accountQ.trim() || undefined,  // Changed from allegroQ
        companyFilter === 'all' ? undefined : companyFilter,  // NOWE
        itemsPerPage,
        offset
      );
      setItems(data.items);
      setTotalItems(data.total);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Nie udało się załadować draftów.");
    } finally {
      setLoading(false);
    }
  }, [q, accountQ, companyFilter, currentPage]);

  useEffect(() => { load(); }, [load]);

  // Reset to page 1 when search terms change
  useEffect(() => {
    setCurrentPage(1);
  }, [q, accountQ, companyFilter]);
  useEffect(() => {
    const ids = Array.from(new Set(items.map(i => i.fakturownia_client_id).filter(Boolean)));
    const missing = ids.filter(id => !(id in fcMap));
    if (!missing.length) return;

    (async () => {
      try {
        const arr = await Promise.all(
          missing.map(id =>
            api.get("/fakturownia-clients", { params: { id } })
              .then(res => res.data?.[0])
              .catch(() => null)
          )
        );
        const map: Record<number, FakturowniaClient> = {};
        arr.forEach(c => { if (c?.id) map[c.id] = c; });
        setFcMap(prev => ({ ...prev, ...map }));
      } catch { /* no-op */ }
    })();
  }, [items, fcMap]);

  // Fetch Bitrix deal info for all accounts in current drafts
  useEffect(() => {
    const hasAccounts = items.some(d => d.ads_accounts?.length);
    if (!hasAccounts) return;

    (async () => {
      try {
        // Fetch both Allegro and Google Ads accounts with their Bitrix mappings
        const [allegroDeals, googleDeals] = await Promise.all([
          getAccountDeals("allegro").catch(() => []),
          getAccountDeals("google_ads").catch(() => []),
        ]);

        const allDeals = [...allegroDeals, ...googleDeals];
        const dealsMap: Record<string, AccountWithDeal> = {};
        allDeals.forEach((deal) => {
          // Key by both client_id (UUID) and display_name (nickname) for Allegro compatibility
          const keyById = `${deal.source_type}-${deal.client_id}`;
          const keyByName = `${deal.source_type}-${deal.display_name}`;
          dealsMap[keyById] = deal;
          dealsMap[keyByName] = deal;
        });
        setBitrixDeals(dealsMap);
      } catch {
        // Silent fail - Bitrix info is not critical
      }
    })();
  }, [items]);

  // Auto-refresh Bitrix deals when returning from edit page (visibility change)
  useEffect(() => {
    const handleVisibilityChange = async () => {
      if (document.visibilityState === "visible") {
        try {
          const [allegroDeals, googleDeals] = await Promise.all([
            getAccountDeals("allegro").catch(() => []),
            getAccountDeals("google_ads").catch(() => []),
          ]);
          const allDeals = [...allegroDeals, ...googleDeals];
          const dealsMap: Record<string, AccountWithDeal> = {};
          allDeals.forEach((deal) => {
            const keyById = `${deal.source_type}-${deal.client_id}`;
            const keyByName = `${deal.source_type}-${deal.display_name}`;
            dealsMap[keyById] = deal;
            dealsMap[keyByName] = deal;
          });
          setBitrixDeals(dealsMap);
        } catch { /* silent */ }
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => document.removeEventListener("visibilitychange", handleVisibilityChange);
  }, []);

  // Refresh Bitrix deals after assignment change
  const refreshBitrixDeals = async () => {
    try {
      const [allegroDeals, googleDeals] = await Promise.all([
        getAccountDeals("allegro").catch(() => []),
        getAccountDeals("google_ads").catch(() => []),
      ]);
      const allDeals = [...allegroDeals, ...googleDeals];
      const dealsMap: Record<string, AccountWithDeal> = {};
      allDeals.forEach((deal) => {
        // Key by both client_id (UUID) and display_name (nickname) for Allegro compatibility
        const keyById = `${deal.source_type}-${deal.client_id}`;
        const keyByName = `${deal.source_type}-${deal.display_name}`;
        dealsMap[keyById] = deal;
        dealsMap[keyByName] = deal;
      });
      setBitrixDeals(dealsMap);
    } catch { /* silent */ }
  };

  const handleAssignBitrixDeal = async (
    sourceType: "allegro" | "google_ads",
    clientId: string,
    deal: BitrixDeal
  ) => {
    try {
      await assignDealToAccount(sourceType, clientId, deal.id);
      await refreshBitrixDeals();
      setEditingBitrixAccount(null);
    } catch {
      setError("Nie udało się przypisać deala Bitrix.");
    }
  };

  const handleClearBitrixDeal = async (
    sourceType: "allegro" | "google_ads",
    clientId: string
  ) => {
    try {
      await assignDealToAccount(sourceType, clientId, null);
      await refreshBitrixDeals();
      setEditingBitrixAccount(null);
    } catch {
      setError("Nie udało się usunąć przypisania deala Bitrix.");
    }
  };

  // Account management handlers
  const handleAddAccountToDraft = async (
    draftId: number,
    sourceType: "allegro" | "google_ads",
    clientId: string
  ) => {
    try {
      await addAccountToDraft(draftId, sourceType, clientId);
      // Refresh drafts list to show new account
      await load();
      await refreshBitrixDeals();
    } catch (e: any) {
      throw new Error(e?.response?.data?.detail || "Nie udało się dodać konta.");
    }
  };

  const handleRemoveAccountFromDraft = async (
    draftId: number,
    sourceType: string,
    clientId: string
  ) => {
    if (!window.confirm("Czy na pewno chcesz usunąć to konto z draftu?")) return;
    try {
      await removeAccountFromDraft(draftId, sourceType, clientId);
      // Refresh drafts list
      await load();
      await refreshBitrixDeals();
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Nie udało się usunąć konta.");
    }
  };

  const toggle = (id: number) => setSelected((s) => ({ ...s, [id]: !s[id] }));

  const toggleAll = async () => {
    if (anySelected) {
      // Odznacz wszystkie
      setSelected({});
    } else {
      // Zaznacz WSZYSTKIE drafty (pobierz wszystkie IDs z serwera)
      try {
        const fcIdMaybe = /^\d+$/.test(q.trim()) ? Number(q.trim()) : undefined;
        const allData = await listDrafts(
          fcIdMaybe,
          fcIdMaybe ? undefined : q.trim() || undefined,
          accountQ.trim() || undefined,
          companyFilter === 'all' ? undefined : companyFilter,  // NOWE
          1000,  // Fetch max 1000 items for Select All
          0
        );
        const newSelected: Record<number, boolean> = {};
        allData.items.forEach(d => {
          newSelected[d.id] = true;
        });
        setSelected(newSelected);
      } catch (e: any) {
        setError("Nie udało się zaznaczyć wszystkich.");
      }
    }
  };

  const handlePreview = async (id: number, monthKey?: string) => {
    setError(null);

    if (previews[id]) {
      setPreviews(m => {
        const copy = { ...m };
        delete copy[id];
        return copy;
      });
      return;
    }

    setPreviewLoading((m) => ({ ...m, [id]: true }));
    try {
      const p = await previewDraft(id, monthKey);
      setPreviews((m) => ({ ...m, [id]: p }));
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Nie udało się pobrać podglądu draftu.");
      setPreviews((m) => ({ ...m, [id]: null }));
    } finally {
      setPreviewLoading((m) => ({ ...m, [id]: false }));
    }
  };

  const handleDelete = async (id: number) => {
    const ok = window.confirm(`Usunąć draft #${id}?`);
    if (!ok) return;
    try {
      await deleteDraft(id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.detail ?? "Nie udało się usunąć draftu.");
    }
  };

  const issueSelected = async () => {
    setError(null);
    const ids = Object.entries(selected).filter(([_, v]) => v).map(([k]) => Number(k));
    if (!ids.length) return;

    try {
      const previewsArr: PreviewOfDraft[] = [];
      for (const id of ids) {
        // ZAWSZE pobieraj świeże preview (nie używaj cache) aby mieć aktualne department_name
        const p = await previewDraft(id);
        previewsArr.push(p);
      }
      const payload = {
        invoices: previewsArr.map((p) => ({
          client_id: p.client_id,
          type: p.type,
          department_name: p.department_name || 'semtop',  // NOWE: przekaż firmę
          positions: p.positions.map(pos => ({
            name: pos.name,
            amount_netto: pos.amount_netto,
            vat: pos.vat,
            amount_brutto: pos.amount_brutto,
            description: pos.description ?? "",
            ...(pos.gtu_code ? { gtu_code: pos.gtu_code } : {}),
            // Account tracking fields (not sent to Fakturownia, only for our database)
            ...(pos.type ? { type: pos.type } : {}),
            ...(pos.nickname ? { nickname: pos.nickname } : {}),
            ...(pos.customer_id ? { customer_id: pos.customer_id } : {})
          })),
          // Pass all accounts from draft (includes both position-derived and manually added)
          ads_accounts: p.ads_accounts?.map(acc => ({
            source_type: acc.source_type,
            client_id: acc.client_id,
            display_name: acc.display_name,
          })),
        })),
      };
      const res = await api.post("/invoices/issue", payload);
      const ok = res.data?.results?.filter((r: any) => r.status === "success") || [];
      const newIds: number[] = ok.map((r: any) => r.invoice_id).filter(Boolean);

      const prev: number[] = JSON.parse(localStorage.getItem("issued_invoice_ids") || "[]");
      const next = Array.from(new Set([...prev, ...newIds]));
      localStorage.setItem("issued_invoice_ids", JSON.stringify(next));

      alert(`Wystawiono: ${ok.length} faktur(y).`);
    } catch (e: any) {
      setError(e?.response?.data?.detail || "Nie udało się wystawić zaznaczonych faktur.");
    }
  };

  if (loading && items.length === 0) {
    return (
      <div style={{
        maxWidth: 1400,
        margin: "0 auto",
        padding: spacing.xxl,
        textAlign: "center",
      }}>
        <div style={{
          fontSize: typography.fontSize.xl,
          fontWeight: typography.fontWeight.semibold,
          color: colors.textSecondary,
        }}>
          ⏳ Ładowanie draftów...
        </div>
      </div>
    );
  }

  return (
    <div style={{
      maxWidth: 1400,
      margin: "0 auto",
      padding: spacing.xxl,
    }}>
      <h2 style={{
        fontSize: typography.fontSize.xxxl,
        fontWeight: typography.fontWeight.bold,
        color: colors.textPrimary,
        marginBottom: spacing.xl,
      }}>
        📝 Drafty faktur
      </h2>

      {error && (
        <div style={{
          padding: spacing.lg,
          marginBottom: spacing.xl,
          background: colors.errorLight,
          color: colors.error,
          border: `1px solid ${colors.error}`,
          borderRadius: borderRadius.md,
          fontWeight: typography.fontWeight.semibold,
          fontSize: typography.fontSize.base,
        }}>
          ⚠️ {error}
        </div>
      )}

      {/* Filters */}
      <div style={{
        marginBottom: spacing.xl,
        padding: spacing.lg,
        background: colors.backgroundAlt,
        borderRadius: borderRadius.md,
        border: `1px solid ${colors.border}`,
        display: "flex",
        gap: spacing.md,
        alignItems: "flex-end",
        flexWrap: "wrap",
      }}>
        <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs, flex: "1 1 300px" }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
            Klient Fakturowni (ID/nazwa/e-mail/skrót):
          </span>
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{
              padding: spacing.sm,
              border: `1px solid ${colors.border}`,
              borderRadius: borderRadius.sm,
              fontSize: typography.fontSize.sm,
            }}
          />
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs, flex: "1 1 200px" }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
            Konto Ads:
          </span>
          <input
            value={accountQ}
            onChange={(e) => setAccountQ(e.target.value)}
            placeholder="Szukaj po nazwie klienta"
            style={{
              padding: spacing.sm,
              border: `1px solid ${colors.border}`,
              borderRadius: borderRadius.sm,
              fontSize: typography.fontSize.sm,
            }}
          />
        </label>

        <label style={{ display: "flex", flexDirection: "column", gap: spacing.xs, flex: "0 1 150px" }}>
          <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold }}>
            Firma:
          </span>
          <select
            value={companyFilter}
            onChange={(e) => setCompanyFilter(e.target.value as any)}
            style={{
              padding: spacing.sm,
              border: `1px solid ${colors.border}`,
              borderRadius: borderRadius.sm,
              fontSize: typography.fontSize.sm,
            }}
          >
            <option value="all">Wszystkie</option>
            <option value="semtop">Semtop</option>
            <option value="adtarget">AdTarget</option>
          </select>
        </label>

        <button
          onClick={load}
          disabled={loading}
          style={createButtonStyle('secondary', loading)}
        >
          {loading ? "⏳ Ładuję…" : "🔍 Szukaj"}
        </button>

        <button
          onClick={toggleAll}
          disabled={items.length === 0}
          style={{
            ...createButtonStyle('secondary', items.length === 0),
            marginLeft: "auto",
          }}
        >
          {anySelected ? "☐ Odznacz wszystkie" : "☑ Zaznacz wszystkie"}
        </button>

        <button
          onClick={issueSelected}
          disabled={!anySelected}
          style={createButtonStyle('success', !anySelected)}
        >
          ✅ Wystaw zaznaczone ({Object.values(selected).filter(Boolean).length})
        </button>
      </div>

      {/* Results count and pagination info */}
      {items.length > 0 && (
        <div style={{
          marginBottom: spacing.md,
          fontSize: typography.fontSize.sm,
          color: colors.textSecondary,
        }}>
          Znaleziono: <strong>{items.length}</strong> {items.length === 1 ? "draft" : "draftów"}
          {totalPages > 1 && ` (strona ${currentPage} z ${totalPages})`}
        </div>
      )}

      {/* Drafts cards */}
      {items.length > 0 ? (
        <div style={{ display: "flex", flexDirection: "column", gap: spacing.lg }}>
          {items.map(d => (
            <DraftRow
              key={d.id}
              d={d}
              selected={!!selected[d.id]}
              toggle={() => toggle(d.id)}
              onPreview={handlePreview}
              onDelete={() => handleDelete(d.id)}
              preview={previews[d.id] || null}
              previewLoading={!!previewLoading[d.id]}
              fcMap={fcMap}
              bitrixDeals={bitrixDeals}
              editingBitrixAccount={editingBitrixAccount}
              setEditingBitrixAccount={setEditingBitrixAccount}
              onAssignBitrixDeal={handleAssignBitrixDeal}
              onClearBitrixDeal={handleClearBitrixDeal}
              onAddAccount={handleAddAccountToDraft}
              onRemoveAccount={handleRemoveAccountFromDraft}
            />
          ))}
        </div>
      ) : (
        <div style={{
          padding: spacing.xxxl,
          textAlign: "center",
          background: colors.backgroundAlt,
          borderRadius: borderRadius.md,
          border: `2px dashed ${colors.border}`,
        }}>
          <div style={{ fontSize: typography.fontSize.xl, marginBottom: spacing.md }}>📭</div>
          <div style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold, marginBottom: spacing.xs }}>
            Brak draftów
          </div>
          <div style={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
            {q || accountQ ? "Spróbuj zmienić kryteria wyszukiwania" : "Nie znaleziono żadnych draftów faktur"}
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          marginTop: spacing.xl,
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          gap: spacing.md,
        }}>
          <button
            onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
            disabled={currentPage === 1}
            style={createButtonStyle('secondary', currentPage === 1)}
          >
            ← Poprzednia
          </button>

          <div style={{
            display: "flex",
            gap: spacing.xs,
            flexWrap: "wrap",
          }}>
            {generatePageNumbers(currentPage, totalPages).map((page, index) => (
              page === '...' ? (
                <span
                  key={`ellipsis-${index}`}
                  style={{
                    padding: '8px 12px',
                    color: colors.textSecondary,
                    display: 'flex',
                    alignItems: 'center',
                  }}
                >
                  ...
                </span>
              ) : (
                <button
                  key={page}
                  onClick={() => setCurrentPage(page as number)}
                  style={{
                    ...(page === currentPage ? createButtonStyle('primary') : createButtonStyle('secondary')),
                    minWidth: 40,
                  }}
                >
                  {page}
                </button>
              )
            ))}
          </div>

          <button
            onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
            disabled={currentPage === totalPages}
            style={createButtonStyle('secondary', currentPage === totalPages)}
          >
            Następna →
          </button>
        </div>
      )}
    </div>
  );
};

const DraftRow = ({
  d, selected, toggle, onPreview, onDelete, preview, previewLoading, fcMap, bitrixDeals,
  editingBitrixAccount, setEditingBitrixAccount, onAssignBitrixDeal, onClearBitrixDeal,
  onAddAccount, onRemoveAccount,
}: {
  d: Draft;
  selected: boolean;
  toggle: () => void;
  onPreview: (id: number) => void;
  onDelete: () => void;
  preview: PreviewOfDraft | null;
  previewLoading: boolean;
  fcMap: Record<number, FakturowniaClient>;
  editingBitrixAccount: { sourceType: string; clientId: string } | null;
  setEditingBitrixAccount: (val: { sourceType: string; clientId: string } | null) => void;
  onAssignBitrixDeal: (sourceType: "allegro" | "google_ads", clientId: string, deal: BitrixDeal) => void;
  onClearBitrixDeal: (sourceType: "allegro" | "google_ads", clientId: string) => void;
  bitrixDeals: Record<string, AccountWithDeal>;
  onAddAccount: (draftId: number, sourceType: "allegro" | "google_ads", clientId: string) => Promise<void>;
  onRemoveAccount: (draftId: number, sourceType: string, clientId: string) => Promise<void>;
}) => {
  // State for add account form
  const [showAddAccount, setShowAddAccount] = useState(false);
  const [newAccountType, setNewAccountType] = useState<"allegro" | "google_ads">("allegro");
  const [newAccountId, setNewAccountId] = useState("");
  const [newAccountDisplayName, setNewAccountDisplayName] = useState("");
  const [addingAccount, setAddingAccount] = useState(false);
  const [addAccountError, setAddAccountError] = useState<string | null>(null);
  const [addAccountSearchQuery, setAddAccountSearchQuery] = useState("");
  const [addAccountOptions, setAddAccountOptions] = useState<Client[]>([]);
  const [addAccountSearchBusy, setAddAccountSearchBusy] = useState(false);
  const addAccountDebounceRef = useRef<number | undefined>(undefined);

  /** Search accounts for add account dropdown */
  const handleAddAccountSearch = (query: string) => {
    setAddAccountSearchQuery(query);
    setAddAccountOptions([]);

    if (addAccountDebounceRef.current) {
      window.clearTimeout(addAccountDebounceRef.current);
    }

    if (query.trim().length < 2) return;

    addAccountDebounceRef.current = window.setTimeout(async () => {
      try {
        setAddAccountSearchBusy(true);
        let resp;
        if (newAccountType === "google_ads") {
          resp = await api.get<Client[]>("/google-ads/customers", {
            params: { search: query.trim() },
          });
        } else {
          resp = await api.get<Client[]>("/allegro-clients", {
            params: { nickname: query.trim() },
          });
        }
        setAddAccountOptions(Array.isArray(resp.data) ? resp.data : []);
      } catch {
        setAddAccountOptions([]);
      } finally {
        setAddAccountSearchBusy(false);
      }
    }, 250);
  };

  /** Select account from search dropdown */
  const handleSelectAddAccount = (c: Client) => {
    if (newAccountType === "google_ads") {
      const gc = c as GoogleAdsClient;
      setNewAccountId(gc.customer_id);
      setNewAccountDisplayName(gc.descriptive_name || gc.customer_id);
      setAddAccountSearchQuery(gc.descriptive_name || gc.customer_id);
    } else {
      const ac = c as AllegroClient;
      setNewAccountId(ac.id);
      setNewAccountDisplayName(ac.nickname);
      setAddAccountSearchQuery(ac.nickname);
    }
    setAddAccountOptions([]);
  };

  const handleAddAccount = async () => {
    if (!newAccountId.trim()) {
      setAddAccountError("Wybierz konto z listy");
      return;
    }
    setAddingAccount(true);
    setAddAccountError(null);
    try {
      await onAddAccount(d.id, newAccountType, newAccountId.trim());
      setNewAccountId("");
      setNewAccountDisplayName("");
      setAddAccountSearchQuery("");
      setShowAddAccount(false);
    } catch (err: any) {
      setAddAccountError(err.message || "Nie udało się dodać konta");
    } finally {
      setAddingAccount(false);
    }
  };

  const clientInfo = (() => {
    const c = fcMap[d.fakturownia_client_id];
    if (c) {
      return {
        name: c.name,
        email: c.email,
        shortcut: c.shortcut,
        tax_no: c.tax_no,
      };
    }
    return {
      name: d.fakturownia_client_name || `ID: ${d.fakturownia_client_id}`,
    };
  })();

  // Check if this card contains an account being edited (for z-index)
  const isEditingInThisCard = editingBitrixAccount && d.ads_accounts?.some(
    acc => acc.source_type === editingBitrixAccount.sourceType && acc.client_id === editingBitrixAccount.clientId
  );

  return (
    <div style={{
      padding: spacing.lg,
      background: colors.backgroundAlt,
      borderRadius: borderRadius.md,
      border: `1px solid ${colors.border}`,
      boxShadow: shadows.sm,
      transition: "all 0.2s ease",
      position: "relative",
      zIndex: isEditingInThisCard ? 1000 : "auto",
    }}>
      {/* Header */}
      <div style={{
        display: "flex",
        alignItems: "center",
        gap: spacing.md,
        marginBottom: spacing.md,
        flexWrap: "wrap",
      }}>
        <input
          type="checkbox"
          checked={selected}
          onChange={toggle}
          style={{
            width: 18,
            height: 18,
            cursor: "pointer",
          }}
        />

        <div style={{ flex: "1 1 auto", minWidth: 200 }}>
          <div style={{
            fontSize: typography.fontSize.lg,
            fontWeight: typography.fontWeight.bold,
            color: colors.textPrimary,
            marginBottom: spacing.xs,
            display: 'flex',
            alignItems: 'center',
            gap: spacing.sm,
          }}>
            {d.name}
            <span style={{
              display: 'inline-block',
              padding: `${spacing.xs}px ${spacing.sm}px`,
              background: d.department_name === 'semtop' ? colors.primaryLight : colors.warningLight,
              color: d.department_name === 'semtop' ? colors.primary : colors.warning,
              borderRadius: borderRadius.sm,
              fontSize: typography.fontSize.xs,
              fontWeight: typography.fontWeight.semibold,
            }}>
              {d.department_name === 'semtop' ? 'Semtop' : 'AdTarget'}
            </span>
          </div>
          <div style={{
            fontSize: typography.fontSize.sm,
            color: colors.textSecondary,
          }}>
            Draft #{d.id}
          </div>
        </div>

        <div style={{ display: "flex", gap: spacing.sm, flexWrap: "wrap" }}>
          <button
            onClick={() => onPreview(d.id)}
            disabled={previewLoading}
            style={createButtonStyle('secondary', previewLoading)}
          >
            {previewLoading ? "⏳" : preview ? "🔼 Ukryj" : "🔽 Podgląd"}
          </button>

          <a
            href={`/drafts/${d.id}/edit`}
            style={{
              ...createButtonStyle('primary'),
              textDecoration: "none",
              display: "inline-block",
            }}
          >
            ✏️ Edytuj
          </a>

          <button
            onClick={onDelete}
            style={createButtonStyle('danger')}
          >
            🗑️ Usuń
          </button>
        </div>
      </div>

      {/* Details */}
      <div style={{
        display: "grid",
        gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
        gap: spacing.md,
        marginTop: spacing.md,
        paddingTop: spacing.md,
        borderTop: `1px solid ${colors.border}`,
      }}>
        <div>
          <div style={{
            fontSize: typography.fontSize.xs,
            color: colors.textSecondary,
            textTransform: "uppercase",
            marginBottom: spacing.xs,
            fontWeight: typography.fontWeight.semibold,
          }}>
            Klient Fakturowni
          </div>
          <div style={{
            fontSize: typography.fontSize.sm,
            color: colors.textPrimary,
          }}>
            <div style={{ fontWeight: typography.fontWeight.semibold }}>{clientInfo.name}</div>
            {clientInfo.email && <div style={{ fontSize: typography.fontSize.xs, color: colors.textSecondary }}>{clientInfo.email}</div>}
            {clientInfo.shortcut && <div style={{ fontSize: typography.fontSize.xs, color: colors.textSecondary }}>[{clientInfo.shortcut}]</div>}
            {clientInfo.tax_no && <div style={{ fontSize: typography.fontSize.xs, color: colors.textSecondary }}>NIP: {clientInfo.tax_no}</div>}
          </div>
        </div>

        <div style={{ gridColumn: "1 / -1" }}>
          <div style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginBottom: spacing.sm,
          }}>
            <div style={{
              fontSize: typography.fontSize.xs,
              color: colors.textSecondary,
              textTransform: "uppercase",
              fontWeight: typography.fontWeight.semibold,
            }}>
              Konta Ads - Bitrix
            </div>
            <button
              onClick={() => setShowAddAccount(!showAddAccount)}
              style={{
                ...createButtonStyle("primary"),
                padding: `${spacing.xs}px ${spacing.sm}px`,
                fontSize: typography.fontSize.xs,
              }}
            >
              {showAddAccount ? "Anuluj" : "+ Dodaj konto"}
            </button>
          </div>

          {showAddAccount && (
            <div style={{
              padding: spacing.md,
              background: colors.background,
              borderRadius: borderRadius.sm,
              border: `1px solid ${colors.border}`,
              marginBottom: spacing.sm,
            }}>
              <div style={{ display: "flex", gap: spacing.md, alignItems: "center" }}>
                <select
                  value={newAccountType}
                  onChange={(e) => {
                    setNewAccountType(e.target.value as "allegro" | "google_ads");
                    setAddAccountSearchQuery("");
                    setAddAccountOptions([]);
                    setNewAccountId("");
                    setNewAccountDisplayName("");
                  }}
                  style={{
                    padding: `${spacing.sm}px ${spacing.md}px`,
                    border: `1px solid ${colors.border}`,
                    borderRadius: borderRadius.sm,
                    fontSize: typography.fontSize.sm,
                    minWidth: 120,
                    height: 38,
                  }}
                >
                  <option value="allegro">Allegro</option>
                  <option value="google_ads">Google Ads</option>
                </select>
                <div style={{ position: "relative", width: 280 }}>
                  <input
                    type="text"
                    value={addAccountSearchQuery}
                    onChange={(e) => {
                      handleAddAccountSearch(e.target.value);
                      if (newAccountId && e.target.value !== newAccountDisplayName) {
                        setNewAccountId("");
                        setNewAccountDisplayName("");
                      }
                    }}
                    placeholder="Wyszukaj konto..."
                    style={{
                      width: "100%",
                      padding: `${spacing.sm}px ${spacing.md}px`,
                      border: `1px solid ${newAccountId ? colors.success : colors.border}`,
                      borderRadius: borderRadius.sm,
                      fontSize: typography.fontSize.sm,
                      background: newAccountId ? colors.successLight : colors.background,
                      height: 38,
                      boxSizing: "border-box",
                    }}
                  />
                  {addAccountSearchBusy && (
                    <span style={{
                      position: "absolute",
                      right: spacing.sm,
                      top: "50%",
                      transform: "translateY(-50%)",
                      fontSize: typography.fontSize.sm,
                      color: colors.textSecondary,
                    }}>
                      ⏳
                    </span>
                  )}
                  {addAccountOptions.length > 0 && (
                    <div style={{
                      position: "absolute",
                      top: "100%",
                      left: 0,
                      right: 0,
                      background: colors.background,
                      border: `1px solid ${colors.border}`,
                      borderRadius: borderRadius.sm,
                      boxShadow: shadows.md,
                      zIndex: 100,
                      maxHeight: 200,
                      overflowY: "auto",
                    }}>
                      {addAccountOptions.map((c) => {
                        const isGoogle = newAccountType === "google_ads";
                        const gc = c as GoogleAdsClient;
                        const ac = c as AllegroClient;
                        const name = isGoogle ? (gc.descriptive_name || gc.customer_id) : ac.nickname;
                        const cid = isGoogle ? gc.customer_id : ac.id;
                        return (
                          <div
                            key={cid}
                            onClick={() => handleSelectAddAccount(c)}
                            style={{
                              padding: `${spacing.sm}px ${spacing.md}px`,
                              cursor: "pointer",
                              borderBottom: `1px solid ${colors.border}`,
                              fontSize: typography.fontSize.sm,
                            }}
                            onMouseEnter={(e) => e.currentTarget.style.background = colors.gray100}
                            onMouseLeave={(e) => e.currentTarget.style.background = colors.background}
                          >
                            <div style={{ fontWeight: typography.fontWeight.medium }}>{name}</div>
                            <div style={{ fontSize: typography.fontSize.xs, color: colors.textSecondary }}>
                              ID: {cid}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>
                {newAccountId && (
                  <span style={{
                    fontSize: typography.fontSize.sm,
                    color: colors.success,
                    whiteSpace: "nowrap",
                  }}>
                    ✓ {newAccountDisplayName}
                  </span>
                )}
                <button
                  onClick={handleAddAccount}
                  disabled={addingAccount || !newAccountId.trim()}
                  style={{
                    ...createButtonStyle("success"),
                    padding: `${spacing.sm}px ${spacing.lg}px`,
                    fontSize: typography.fontSize.sm,
                    height: 38,
                    minWidth: 80,
                    opacity: addingAccount || !newAccountId.trim() ? 0.5 : 1,
                  }}
                >
                  {addingAccount ? "..." : "Dodaj"}
                </button>
              </div>
              {addAccountError && (
                <div style={{
                  marginTop: spacing.sm,
                  padding: spacing.sm,
                  background: colors.errorLight,
                  color: colors.errorDark,
                  borderRadius: borderRadius.sm,
                  fontSize: typography.fontSize.sm,
                }}>
                  {addAccountError}
                </div>
              )}
            </div>
          )}

          {d.ads_accounts?.length ? (
            <div style={{ overflow: "visible" }}>
              <table style={{
                width: "100%",
                borderCollapse: "collapse",
                fontSize: typography.fontSize.sm,
              }}>
                <thead>
                  <tr style={{ background: colors.gray50 }}>
                    <th style={{ ...th, fontSize: typography.fontSize.xs }}>Konto</th>
                    <th style={{ ...th, fontSize: typography.fontSize.xs, minWidth: 280 }}>Deal Bitrix</th>
                    <th style={{ ...th, fontSize: typography.fontSize.xs }}>Etap</th>
                    <th style={{ ...th, fontSize: typography.fontSize.xs }}>Budżet</th>
                    <th style={{ ...th, fontSize: typography.fontSize.xs }}>Notatka</th>
                    <th style={{ ...th, fontSize: typography.fontSize.xs, minWidth: 130 }}>Akcje</th>
                  </tr>
                </thead>
                <tbody>
                  {d.ads_accounts.map(acc => {
                    const key = `${acc.source_type}-${acc.client_id}`;
                    const bitrixData = bitrixDeals[key];
                    const deal = bitrixData?.bitrix_deal;
                    const isEditing = editingBitrixAccount?.sourceType === acc.source_type &&
                                      editingBitrixAccount?.clientId === acc.client_id;
                    const icon = acc.source_type === "google_ads" ? (
                      <svg width="14" height="14" viewBox="0 0 16 16" style={{ flexShrink: 0 }}>
                        <circle cx="8" cy="8" r="7" fill="#4285F4"/>
                        <text x="8" y="11.5" fontSize="10" fontWeight="bold" fill="white" textAnchor="middle" fontFamily="Arial, sans-serif">G</text>
                      </svg>
                    ) : (
                      <svg width="14" height="14" viewBox="0 0 16 16" style={{ flexShrink: 0 }}>
                        <circle cx="8" cy="8" r="7" fill="#FF8C00"/>
                        <text x="8" y="11.5" fontSize="10" fontWeight="bold" fill="white" textAnchor="middle" fontFamily="Arial, sans-serif">A</text>
                      </svg>
                    );
                    return (
                      <tr key={acc.client_id} style={{ borderBottom: `1px solid ${colors.border}` }}>
                        <td style={td}>
                          <div style={{ display: "flex", alignItems: "center", gap: spacing.xs }}>
                            {icon}
                            <span>{acc.display_name}</span>
                          </div>
                        </td>
                        <td style={{ ...td, position: "relative", overflow: "visible" }}>
                          {isEditing ? (
                            <div style={{ width: "100%" }}>
                              <BitrixDealSearch
                                currentDeal={deal}
                                onSelect={(selectedDeal) =>
                                  onAssignBitrixDeal(
                                    acc.source_type as "allegro" | "google_ads",
                                    acc.client_id,
                                    selectedDeal
                                  )
                                }
                                onClear={() =>
                                  onClearBitrixDeal(
                                    acc.source_type as "allegro" | "google_ads",
                                    acc.client_id
                                  )
                                }
                              />
                            </div>
                          ) : (
                            <span>
                              {deal?.title || <span style={{ color: colors.textSecondary }}>Nie przypisano</span>}
                            </span>
                          )}
                        </td>
                        <td style={td}>
                          {deal?.stage_name || <span style={{ color: colors.textSecondary }}>-</span>}
                        </td>
                        <td style={td}>
                          {deal?.budget !== null && deal?.budget !== undefined
                            ? deal.budget.toLocaleString("pl-PL", { style: "currency", currency: "PLN" })
                            : <span style={{ color: colors.textSecondary }}>-</span>}
                        </td>
                        <td style={{ ...td, maxWidth: 300, wordWrap: "break-word", whiteSpace: "normal" }}>
                          {deal?.budget_note || <span style={{ color: colors.textSecondary }}>-</span>}
                        </td>
                        <td style={td}>
                          <div style={{ display: "flex", gap: spacing.xs, flexWrap: "wrap" }}>
                            {isEditing ? (
                              <button
                                onClick={() => setEditingBitrixAccount(null)}
                                style={{
                                  ...createButtonStyle("secondary"),
                                  padding: `${spacing.xs}px ${spacing.sm}px`,
                                  fontSize: typography.fontSize.xs,
                                }}
                              >
                                Anuluj
                              </button>
                            ) : (
                              <button
                                onClick={() => setEditingBitrixAccount({ sourceType: acc.source_type, clientId: acc.client_id })}
                                style={{
                                  ...createButtonStyle(deal ? "secondary" : "primary"),
                                  padding: `${spacing.xs}px ${spacing.sm}px`,
                                  fontSize: typography.fontSize.xs,
                                }}
                              >
                                {deal ? "Zmień" : "Przypisz"}
                              </button>
                            )}
                            {/* Only show delete button for manually added accounts (not from positions) */}
                            {!acc.is_from_position && (
                              <button
                                onClick={() => onRemoveAccount(d.id, acc.source_type, acc.client_id)}
                                title="Usuń konto z draftu (dodane ręcznie)"
                                style={{
                                  ...createButtonStyle("secondary"),
                                  padding: `${spacing.xs}px ${spacing.sm}px`,
                                  fontSize: typography.fontSize.xs,
                                  color: colors.error,
                                  borderColor: colors.error,
                                  background: colors.errorLight,
                                }}
                              >
                                🗑️
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{ fontSize: typography.fontSize.sm, color: colors.textSecondary }}>
              Brak kont Ads. Użyj "Dodaj konto" aby dodać ręcznie.
            </div>
          )}
        </div>
      </div>

      {/* Preview */}
      {preview && (
        <div style={{
          marginTop: spacing.lg,
          padding: spacing.lg,
          background: colors.background,
          borderRadius: borderRadius.sm,
          border: `1px solid ${colors.border}`,
        }}>
          <h4 style={{
            fontSize: typography.fontSize.base,
            fontWeight: typography.fontWeight.bold,
            marginBottom: spacing.md,
            color: colors.textPrimary,
          }}>
            📋 Podgląd pozycji
          </h4>

          <div style={{ overflowX: "auto" }}>
            <table style={{
              width: "100%",
              borderCollapse: "collapse",
              fontSize: typography.fontSize.sm,
            }}>
              <thead>
                <tr style={{ background: colors.gray50 }}>
                  <th style={{ ...th, fontSize: typography.fontSize.xs }}>Nazwa pozycji</th>
                  <th style={{ ...th, fontSize: typography.fontSize.xs }}>Netto</th>
                  <th style={{ ...th, fontSize: typography.fontSize.xs }}>VAT%</th>
                  <th style={{ ...th, fontSize: typography.fontSize.xs }}>Brutto</th>
                  <th style={{ ...th, fontSize: typography.fontSize.xs }}>Opis</th>
                  <th style={{ ...th, fontSize: typography.fontSize.xs }}>GTU</th>
                  <th style={{ ...th, fontSize: typography.fontSize.xs }}>Szczegóły</th>
                  <th style={{ ...th, fontSize: typography.fontSize.xs }}>Brakujące dni</th>
                </tr>
              </thead>
              <tbody>
                {preview.positions.map((p: any, i: number) => (
                  <tr key={i} style={{ borderBottom: `1px solid ${colors.border}` }}>
                    <td style={td}>{p.name}</td>
                    <td style={td}>{p.amount_netto ?? ""}</td>
                    <td style={td}>{p.vat}%</td>
                    <td style={td}>{p.amount_brutto ?? ""}</td>
                    <td style={{ ...td, maxWidth: 150 }}>{p.description ?? ""}</td>
                    <td style={{ ...td, textAlign: "center", fontWeight: p.gtu_code ? typography.fontWeight.semibold : typography.fontWeight.normal, color: p.gtu_code ? colors.textPrimary : colors.textSecondary }}>{p.gtu_code || "—"}</td>
                    <td style={{ ...td, fontSize: typography.fontSize.xs }}>
                      {p.type === "adjustment" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                          <div><strong>Dni:</strong> {p.days_in_range}</div>
                          <div><strong>Podstawa netto:</strong> {p.podstawa_do_wyrownania ?? "-"}</div>
                          <div><strong>Okres:</strong> {p.date_from ?? "-"} – {p.date_to ?? "-"}</div>
                        </div>
                      )}
                      {p.type === "adjustment_percent" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                          <div>
                            <strong>Budżet:</strong> {Number((p as any).total_cost || 0).toFixed(2)} zł
                            {(p as any).cap_enabled && (p as any).cap_amount && ` (${Number((p as any).cap_amount).toFixed(2)} zł max)`}
                          </div>
                          <div><strong>{(p as any).source_type === "google_ads" ? "Google Ads" : "Allegro Ads"}:</strong> {(p as any).display_name || (p as any).client_id || "-"}</div>
                          <div><strong>Okres:</strong> {p.date_from ?? "-"} – {p.date_to ?? "-"}</div>
                          <div>
                            <strong>%:</strong> {(p as any).percent ?? "-"}, <strong>Próg:</strong> {(p as any).threshold ?? 0} PLN
                            {((p as any).threshold ?? 0) === 0 && <span style={{ marginLeft: "4px", color: "#666", fontStyle: "italic" }}>(cały budżet)</span>}
                          </div>
                        </div>
                      )}
                      {p.type === "percent" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                          <div>
                            <strong>Budżet:</strong> {Number(p.total_cost).toFixed(2)} zł
                            {(p as any).cap_enabled && (p as any).cap_amount && ` (${Number((p as any).cap_amount).toFixed(2)} zł max)`}
                          </div>
                          <div><strong>{p.source_type === "google_ads" ? "Google Ads" : "Allegro Ads"}:</strong> {p.nickname ?? "-"}</div>
                          <div><strong>Okres:</strong> {p.date_from ?? "-"} – {p.date_to ?? "-"}</div>
                          <div>
                            <strong>%:</strong> {p.percent ?? "-"}, <strong>Próg:</strong> {p.threshold ?? 0} PLN
                            {(p.threshold ?? 0) === 0 && <span style={{ marginLeft: "4px", color: "#666", fontStyle: "italic" }}>(cały budżet)</span>}
                          </div>
                          <div><strong>Tryb:</strong> {p.policy === "rolling_30" ? "Umowa miesięczna" : p.policy === "previous_month" ? "Nieokreślony" : (p.policy ?? "-")}</div>
                        </div>
                      )}
                      {p.type === "revenue_percent" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                          <div><strong>Obrót:</strong> {Number((p as any).conversions_value || 0).toFixed(2)} zł</div>
                          <div><strong>{(p as any).source_type === "allegro" ? "Allegro Ads" : "Google Ads"}:</strong> {(p as any).descriptive_name ?? "-"}</div>
                          <div><strong>Okres:</strong> {p.date_from ?? "-"} – {p.date_to ?? "-"}</div>
                          <div><strong>%:</strong> {(p as any).percent ?? "-"}%, <strong>Min:</strong> {(p as any).minimum ?? 0} PLN</div>
                          <div><strong>Wyliczone:</strong> {Number((p as any).calculated_amount || 0).toFixed(2)}, <strong>Użyte:</strong> {Number(p.amount_netto).toFixed(2)} PLN</div>
                        </div>
                      )}
                      {p.type === "budget" && (
                        <div style={{ display: "flex", flexDirection: "column", gap: spacing.xs }}>
                          <div><strong>Konto:</strong> {(p as any).descriptive_name ?? "-"}</div>
                          <div><strong>Źródło:</strong> {(p as any).source_type === "allegro" ? "Allegro Ads" : "Google Ads"}</div>
                          <div><strong>Okres:</strong> {p.date_from ?? "-"} – {p.date_to ?? "-"}</div>
                          <div><strong>Budżet brutto:</strong> {Number((p as any).total_cost_brutto || 0).toFixed(2)} zł</div>
                          <div><strong>Netto (÷1.23):</strong> {Number(p.amount_netto).toFixed(2)} zł</div>
                          <div><strong>Tryb:</strong> {(p as any).policy === "rolling_30" ? "Umowa miesięczna" : (p as any).policy === "previous_month" ? "Nieokreślony" : (p as any).policy === "custom" ? "Custom" : ((p as any).policy ?? "-")}</div>
                        </div>
                      )}
                    </td>
                    <td style={td}>
                      {(p.type !== "percent" && p.type !== "revenue_percent" && p.type !== "budget" && p.type !== "adjustment_percent")
                        ? "—"
                        : (Array.isArray(p.missing_days) && p.missing_days.length > 0)
                          ? (
                            <div>
                              <div style={{ fontWeight: typography.fontWeight.bold, color: colors.warning }}>
                                {p.missing_days.length} {p.missing_days.length === 1 ? "dzień" : "dni"}
                              </div>
                              <div style={{ fontSize: typography.fontSize.xs, maxHeight: 80, overflowY: "auto", marginTop: spacing.xs }}>
                                {p.missing_days.join(", ")}
                              </div>
                            </div>
                          )
                          : <span style={{ color: colors.success }}>✅ Brak</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};


const th: React.CSSProperties = {
  border: `1px solid ${colors.border}`,
  padding: spacing.sm,
  fontWeight: typography.fontWeight.bold,
  background: colors.gray50,
  textAlign: "left",
};
const td: React.CSSProperties = {
  border: `1px solid ${colors.border}`,
  padding: spacing.sm,
  verticalAlign: "top",
};

export default DraftsList;
